import { message } from "antd";

// File type and size rules for every upload on the web app, so each upload
// shows what it accepts (uploadHint) and really refuses anything else
// (uploadProps). Files are sent as base64 in JSON; the APIs accept up to
// 50 MB of body, so these limits stay well under that.

const TYPES = {
  pdf: { ext: ".pdf", mime: ["application/pdf"] },
  jpg: { ext: ".jpg", mime: ["image/jpeg"] },
  jpeg: { ext: ".jpeg", mime: ["image/jpeg"] },
  png: { ext: ".png", mime: ["image/png"] },
  // browsers report CSV in different ways (Excel on Windows: vnd.ms-excel)
  csv: { ext: ".csv", mime: ["text/csv", "application/vnd.ms-excel", "application/csv", "text/plain"] },
};

export const UPLOAD_RULES = {
  // certificates, insurance, statements, meeting minutes
  PDF: { types: ["pdf"], maxMB: 10, label: "PDF only" },
  // certificates that may be a scan or photo
  PDF_OR_IMAGE: { types: ["pdf", "jpg", "jpeg", "png"], maxMB: 10, label: "PDF, JPG or PNG" },
  // Workforce staff documents: up to four go in one request with the staff details
  STAFF_DOCUMENT: { types: ["pdf", "jpg", "jpeg", "png"], maxMB: 5, label: "PDF, JPG or PNG" },
  // manuals can be long documents
  MANUAL: { types: ["pdf"], maxMB: 20, label: "PDF only" },
  // timesheet import
  CSV: { types: ["csv"], maxMB: 2, label: "CSV only" },
};

export const uploadAccept = (rule) => rule.types.map((t) => TYPES[t].ext).join(",");

// e.g. "PDF only · Max 10 MB"
export const uploadHint = (rule) => `${rule.label} · Max ${rule.maxMB} MB`;

// the reason the file is refused, or null when it is fine
export const checkUploadFile = (file, rule) => {
  const name = String((file && file.name) || "").toLowerCase();
  const ext = name.includes(".") ? name.slice(name.lastIndexOf(".") + 1) : "";
  const allowed = rule.types.includes(ext) && (!file.type || TYPES[ext].mime.includes(file.type));
  if (!allowed) return `Please select a ${rule.label.replace(" only", "")} file.`;
  if (file.size > rule.maxMB * 1024 * 1024) return `The file must be ${rule.maxMB} MB or smaller.`;
  return null;
};

// antd 4.10 Upload props. A refused file is never added to the list or read
// (a rejected promise stops it). An accepted one is kept for the form to send
// (beforeUpload false), or passed on to customRequest when autoUpload is set.
export const uploadProps = (rule, { autoUpload = false } = {}) => ({
  accept: uploadAccept(rule),
  beforeUpload: (file) => {
    const error = checkUploadFile(file, rule);
    if (error) {
      message.error(error);
      return Promise.reject(new Error(error));
    }
    return autoUpload;
  },
});
